import { Check } from 'lucide-react'
import type { FollowedShow } from '../../../types/series'
import type { UseCase } from './use-case-step'
import type { MaterialItem } from './materials-step'

interface ConfirmationStepProps {
  data: {
    useCase: UseCase | null
    interests: string
    materials: MaterialItem[]
    followedShows: FollowedShow[]
    negativeKeywords: string
    frequency: string
    episodeDurationMinutes: number
    deliveryTimeLocal: number
    timezone: string
    style: string
    name: string
    description: string
  }
}

const STYLE_LABELS: Record<string, string> = {
  'morning-briefing': 'Morning briefing',
  'two-friends': 'Two friends talking',
  'deep-dive': 'Deep dive',
  storyteller: 'Storyteller',
}

const FREQUENCY_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekdays: 'Weekdays (Mon to Fri)',
  weekly: 'Weekly',
  biweekly: 'Every two weeks',
}

function formatTime(minutesFromMidnight: number): string {
  const hours = Math.floor(minutesFromMidnight / 60)
  const minutes = minutesFromMidnight % 60
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours % 12 || 12
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${ampm}`
}

export function ConfirmationStep({ data }: ConfirmationStepProps) {
  const frequencyLabel = FREQUENCY_LABELS[data.frequency]?.toLowerCase() || data.frequency.toLowerCase()
  const styleLabel = STYLE_LABELS[data.style] || data.style
  const deliveryTime = formatTime(data.deliveryTimeLocal)

  return (
    <div>
      <h2 className="mb-6 text-3xl font-extrabold tracking-tight text-foreground">Ready to go!</h2>
      
      <div className="space-y-6 text-[17px] font-medium leading-relaxed text-foreground/70">
        <p>
          You are creating <strong className="font-extrabold text-foreground">{data.name}</strong>, a podcast about <strong className="font-extrabold text-foreground">{data.interests}</strong>.
        </p>
        
        <p>
          Episodes will be around <strong className="font-extrabold text-foreground">{data.episodeDurationMinutes} minutes</strong> long, delivered to you <strong className="font-extrabold text-foreground">{frequencyLabel}</strong> at <strong className="font-extrabold text-foreground">{deliveryTime}</strong> ({data.timezone}). The show will follow a <strong className="font-extrabold text-foreground">{styleLabel.toLowerCase()}</strong> format.
        </p>

        {(data.materials.length > 0 || data.followedShows.length > 0 || data.negativeKeywords) && (
          <p>
            It will draw inspiration from {data.materials.length > 0 ? `${data.materials.length} material source${data.materials.length === 1 ? '' : 's'}` : ''}
            {data.materials.length > 0 && data.followedShows.length > 0 ? ' and ' : ''}
            {data.followedShows.length > 0 ? `${data.followedShows.length} followed show${data.followedShows.length === 1 ? '' : 's'}` : ''}
            {data.negativeKeywords ? `, while deliberately avoiding topics like ${data.negativeKeywords}` : ''}.
          </p>
        )}
      </div>

      <div className="mt-10 flex items-start gap-4 rounded-3xl bg-brand-50/50 p-6 text-brand-900 border border-brand-100/50 shadow-sm">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-brand-100">
          <Check className="h-5 w-5 text-brand-600" />
        </div>
        <p className="pt-2 text-[15px] font-medium">
          Your first episode is <span className="font-bold">free</span>. Future episodes require a subscription.
        </p>
      </div>
    </div>
  )
}
